"use client";

import { useEffect, useRef, useState, useMemo } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, useInView } from "motion/react";
import Autoplay from "embla-carousel-autoplay";
import {
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  BadgePercent,
  ChevronLeft,
  ChevronRight,
  Coins,
  Clock3,
  Cpu,
  Hourglass,
  Landmark,
  MapPin,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Scale,
  ScrollText,
  Wallet,
  Target,
  Zap,
} from "lucide-react";

import { FaqExplorer } from "@/components/faq/faq-explorer";
import { LANDING_FAQ_ITEMS } from "@/components/faq/faq-data";
import { routeToStartupDestination } from "@/lib/auth-session-bootstrap";
import { FullscreenLoadingScreen } from "@/components/app-shell/fullscreen-loading-screen";
import { formatCurrencyInr } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

// ─── Animation helpers ────────────────────────────────────────────────────────

function FadeUp({
  children,
  delay = 0,
  className,
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

const LANDING_SECTIONS = [
  { id: "home", label: "Home" },
  { id: "overview", label: "Overview" },
  { id: "astrologers", label: "Astrologers" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQs" },
] as const;

// ─── Nav ─────────────────────────────────────────────────────────────────────

function Nav({
  activeSection,
  onTabSelect,
}: {
  activeSection: string;
  onTabSelect: (sectionId: string) => void;
}) {
  return (
    <motion.nav
      animate={{ opacity: 1, y: 0 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-[hsl(var(--line)/0.5)] bg-[hsl(var(--aura)/0.82)] px-4 py-3 backdrop-blur-md md:px-8"
      initial={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden p-0.5 sm:size-14 sm:p-0">
              <img
                alt="AskJyotish"
                className="h-full w-full object-contain"
                src="/app_logo.png"
              />
            </div>
            <span className="font-display text-2xl font-bold text-[hsl(var(--maroon))] sm:text-4xl">
              AskJyotish
            </span>
          </div>

          <Link
            className="inline-flex h-9 items-center gap-1.5 rounded-[1.2rem] bg-[linear-gradient(135deg,hsl(var(--maroon)),hsl(var(--ruby)))] px-4 text-sm font-semibold text-white shadow-[0_4px_16px_hsl(var(--maroon)/0.25)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_hsl(var(--maroon)/0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 md:hidden"
            href="/auth"
          >
            Sign in
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="flex items-center gap-1 overflow-x-auto pb-1 whitespace-nowrap md:justify-center md:gap-3 md:overflow-x-visible md:pb-0">
          {LANDING_SECTIONS.map((section) => {
            const isActive = section.id === activeSection;
            return (
              <a
                className={`shrink-0 rounded-full px-2 py-1.5 text-xs font-medium transition-all sm:px-2.5 sm:text-sm ${
                  isActive
                    ? "bg-[hsl(var(--maroon))] text-white shadow-[0_4px_16px_hsl(var(--maroon)/0.22)]"
                    : "text-[hsl(var(--muted-foreground))] hover:bg-white/70 hover:text-[hsl(var(--ink))]"
                }`}
                href={`#${section.id}`}
                key={section.id}
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById(section.id)
                    ?.scrollIntoView({ behavior: "smooth" });
                  onTabSelect(section.id);
                }}
              >
                {section.label}
              </a>
            );
          })}
        </div>

        <Link
          className="hidden h-9 items-center gap-1.5 rounded-[1.2rem] bg-[linear-gradient(135deg,hsl(var(--maroon)),hsl(var(--ruby)))] px-4 text-sm font-semibold text-white shadow-[0_4px_16px_hsl(var(--maroon)/0.25)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_hsl(var(--maroon)/0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 md:inline-flex"
          href="/auth"
        >
          Sign in
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </motion.nav>
  );
}

// ─── Core features ───────────────────────────────────────────────────────────

const FEATURE_ACCENTS = {
  gold: {
    iconWrap: "bg-[hsl(var(--gold)/0.12)] ring-1 ring-[hsl(var(--gold)/0.25)]",
    iconColor: "text-[hsl(var(--gold))]",
    subtitle: "text-[hsl(var(--gold))]",
    badge:
      "bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] ring-1 ring-[hsl(var(--gold)/0.25)]",
  },
  purple: {
    iconWrap: "bg-purple-500/10 ring-1 ring-purple-600/25",
    iconColor: "text-purple-700",
    subtitle: "text-purple-700",
    badge: "bg-purple-500/10 text-purple-700 ring-1 ring-purple-600/20",
  },
  blue: {
    iconWrap: "bg-blue-500/10 ring-1 ring-blue-600/25",
    iconColor: "text-blue-700",
    subtitle: "text-blue-700",
    badge: "bg-blue-500/10 text-blue-700 ring-1 ring-blue-600/20",
  },
  green: {
    iconWrap: "bg-emerald-600/10 ring-1 ring-emerald-700/25",
    iconColor: "text-emerald-700",
    subtitle: "text-emerald-700",
    badge: "bg-emerald-600/10 text-emerald-700 ring-1 ring-emerald-700/20",
  },
  amber: {
    iconWrap: "bg-amber-500/10 ring-1 ring-amber-600/25",
    iconColor: "text-amber-700",
    subtitle: "text-amber-700",
    badge: "bg-amber-500/10 text-amber-700 ring-1 ring-amber-600/20",
  },
  rose: {
    iconWrap: "bg-rose-500/10 ring-1 ring-rose-600/25",
    iconColor: "text-rose-700",
    subtitle: "text-rose-700",
    badge: "bg-rose-500/10 text-rose-700 ring-1 ring-rose-600/20",
  },
  muted: {
    iconWrap: "bg-stone-500/10 ring-1 ring-stone-600/20",
    iconColor: "text-stone-500",
    subtitle: "text-stone-500",
    badge: "bg-stone-500/10 text-stone-600 ring-1 ring-stone-600/15",
  },
} as const;

const FEATURE_CARDS = [
  {
    icon: ScrollText,
    title: "Vidya Dhana",
    subtitle: "Knowledge is a Gift",
    body: 'In Vedic tradition, sacred knowledge (Vidya) is never "sold." We honor the ancient sages by ensuring our platform does not profit from their ancient wisdom.',
    badgeIcon: Landmark,
    badgeLabel: "Traditional Wisdom",
    accent: "gold" as const,
  },
  {
    icon: Coins,
    title: "Per-question billing",
    subtitle: "Pay As You Ask",
    body: "Ask only what you need. Pricing stays tied to each reading instead of a subscription plan or per minute consultation.",
    badgeIcon: BadgePercent,
    badgeLabel: "Flexible Payments",
    accent: "purple" as const,
  },
  {
    icon: Cpu,
    title: "AI-only astrologer",
    subtitle: "No Human Interference",
    body: "Readings are generated by AI, grounded in Vedic astrology logic, with no manual astrologer intervention.",
    badgeIcon: Cpu,
    badgeLabel: "100% AI-Driven",
    accent: "blue" as const,
  },
  {
    icon: Scale,
    title: "Zero Profit pricing",
    subtitle: "Sustainable Sovereignty",
    body: "You pay only the actual cost of running the system, with no markup and no hidden fees.",
    badgeIcon: BadgePercent,
    badgeLabel: "At Cost",
    accent: "green" as const,
  },
  {
    icon: Clock3,
    title: "Birth time aware reading",
    subtitle: "Flexible Birth Timing",
    body: "Unsure of the exact birth time minute? Our engine identifies the stable patterns within your birth time window for a consistent and reliable reading.",
    badgeIcon: Target,
    badgeLabel: "Minute-Level",
    accent: "amber" as const,
  },
  {
    icon: Hourglass,
    title: "Birth time correction",
    subtitle: "Coming Soon",
    body: "Don't know your exact birth time? Soon, our AI will use past life events to reverse-engineer your chart and mathematically pinpoint the moment of birth.",
    badgeIcon: Sparkles,
    badgeLabel: "Coming Soon",
    accent: "muted" as const,
    disabled: true,
  },
];

function FeatureCards() {
  return (
    <div className="relative mx-auto mt-12 grid w-full max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-5">
      {FEATURE_CARDS.map((feature, i) => {
        const accent = FEATURE_ACCENTS[feature.accent];
        return (
          <FadeUp delay={0.15 + i * 0.08} key={feature.title}>
            <div
              className={`soft-note flex h-full flex-col items-center p-6 text-center ${
                feature.disabled ? "opacity-80" : ""
              }`}
            >
              <div
                className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full ${accent.iconWrap}`}
              >
                <feature.icon className={`h-6 w-6 ${accent.iconColor}`} />
              </div>
              <h3 className="font-display text-lg font-semibold tracking-tight text-[hsl(var(--ink))]">
                {feature.title}
              </h3>
              <p className={`mt-1 text-sm italic ${accent.subtitle}`}>
                {feature.subtitle}
              </p>
              <p className="mt-3 text-[13px] leading-relaxed text-[hsl(var(--muted-foreground))]">
                {feature.body}
              </p>
              <div
                className={`mt-5 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-wider ${accent.badge}`}
              >
                <feature.badgeIcon className="h-3.5 w-3.5" />
                {feature.badgeLabel}
              </div>
            </div>
          </FadeUp>
        );
      })}
    </div>
  );
}

function ScreenshotFrame({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`rounded-[2rem] border border-[rgba(116,102,70,0.16)] bg-[linear-gradient(180deg,rgba(247,236,220,0.96),rgba(255,250,242,0.98))] p-2.5 shadow-[0_24px_70px_hsl(var(--maroon)/0.1),0_4px_16px_hsl(var(--maroon)/0.06)] sm:p-3 ${className ?? ""}`}
    >
      <div className="overflow-hidden rounded-[1.45rem] border border-[rgba(116,102,70,0.1)] bg-[#fffdf8]">
        {children}
      </div>
    </div>
  );
}

function AddPersonScreenshot() {
  return (
    <ScreenshotFrame className="w-full max-w-[1240px]">
      <img
        alt="Add person - birth details form"
        className="block h-auto w-full"
        src="/screenshots/add-person.png"
      />
    </ScreenshotFrame>
  );
}

function RechargeAccountScreenshot() {
  return (
    <ScreenshotFrame className="w-full max-w-[1240px]">
      <img
        alt="Credits and billing recharge account"
        className="block h-auto w-full"
        src="/screenshots/billing.png"
      />
    </ScreenshotFrame>
  );
}

function ChatScreenshot() {
  return (
    <ScreenshotFrame className="w-full max-w-[1240px]">
      <img
        alt="Chat conversation and analysis"
        className="block h-auto w-full"
        src="/screenshots/chat.png"
      />
    </ScreenshotFrame>
  );
}

const ONBOARDING_SLIDES = [
  {
    key: "profile",
    title: "Set up the profile",
    render: AddPersonScreenshot,
  },
  {
    key: "billing",
    title: "Add credits",
    render: RechargeAccountScreenshot,
  },
  {
    key: "chat",
    title: "View the reading",
    render: ChatScreenshot,
  },
] as const;

function OnboardingCarousel() {
  const autoplayPlugin = useMemo(() => {
    return Autoplay({
      delay: 5200,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    });
  }, []);

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) return;

    const handleSelect = () => setActiveIndex(carouselApi.selectedScrollSnap());
    handleSelect();

    carouselApi.on("select", handleSelect);
    carouselApi.on("reInit", handleSelect);

    return () => {
      carouselApi.off("select", handleSelect);
      carouselApi.off("reInit", handleSelect);
    };
  }, [carouselApi]);

  function goToSlide(index: number) {
    const normalized =
      ((index % ONBOARDING_SLIDES.length) + ONBOARDING_SLIDES.length) %
      ONBOARDING_SLIDES.length;
    carouselApi?.scrollTo(normalized);
  }

  return (
    <div className="relative mx-auto w-full max-w-[1120px] lg:max-w-[600px] xl:max-w-[640px]">
      <div className="mx-auto w-full rounded-[20px] border border-[rgba(116,102,70,0.18)] bg-[hsl(var(--panel))] px-3 py-3 shadow-[0_24px_70px_hsl(var(--maroon)/0.08)] sm:px-5 sm:py-5 lg:px-4 lg:py-4">
        <Carousel
          className="relative"
          opts={{ align: "start", loop: true }}
          plugins={[autoplayPlugin]}
          setApi={setCarouselApi}
        >
          <CarouselContent>
            {ONBOARDING_SLIDES.map((slide) => {
              const Slide = slide.render;
              return (
                <CarouselItem key={slide.key}>
                  <div className="relative px-1 sm:px-0">
                    <div className="min-w-0">
                      <Slide />
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        <div className="mt-4 flex items-center justify-between gap-3 sm:mt-6 sm:gap-4">
          <button
            className="inline-flex size-8 items-center justify-center rounded-full border border-[rgba(116,102,70,0.16)] bg-white text-[hsl(var(--muted-foreground))] transition-all hover:bg-[hsl(var(--muted)/0.5)] hover:text-[hsl(var(--ink))] sm:size-9"
            onClick={() => goToSlide(activeIndex - 1)}
            type="button"
          >
            <ChevronLeft className="size-3.5 sm:size-4" />
          </button>

          <div className="flex items-center gap-2">
            {ONBOARDING_SLIDES.map((slide, index) => (
              <button
                aria-label={`Go to ${slide.title}`}
                className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-xs font-semibold transition-all sm:h-9 sm:min-w-9 sm:px-3 sm:text-sm ${
                  index === activeIndex
                    ? "border-[hsl(var(--maroon))] bg-[hsl(var(--maroon))] text-white shadow-[0_4px_12px_hsl(var(--maroon)/0.22)]"
                    : "border-[rgba(116,102,70,0.16)] bg-white text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--gold)/0.35)] hover:text-[hsl(var(--ink))]"
                }`}
                key={slide.key}
                onClick={() => goToSlide(index)}
                type="button"
              >
                {index + 1}
              </button>
            ))}
          </div>

          <button
            className="inline-flex size-8 items-center justify-center rounded-full border border-[rgba(116,102,70,0.16)] bg-white text-[hsl(var(--muted-foreground))] transition-all hover:bg-[hsl(var(--muted)/0.5)] hover:text-[hsl(var(--ink))] sm:size-9"
            onClick={() => goToSlide(activeIndex + 1)}
            type="button"
          >
            <ChevronRight className="size-3.5 sm:size-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section
      className="relative flex min-h-dvh scroll-mt-28 flex-col items-center justify-center overflow-hidden px-4 pt-32 pb-8 sm:px-6 sm:pt-36 sm:pb-12 lg:pt-40 lg:pb-14"
      id="home"
    >
      {/* Warm radial background wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% -10%, hsl(var(--sandal)/0.18) 0%, transparent 70%),
            radial-gradient(ellipse 60% 40% at 80% 100%, hsl(var(--lotus)/0.35) 0%, transparent 60%),
            hsl(var(--aura))
          `,
        }}
      />

      {/* Floating decorative dots */}
      {[
        { top: "18%", left: "8%", size: 5, delay: 0 },
        { top: "70%", left: "5%", size: 3, delay: 1.2 },
        { top: "25%", right: "7%", size: 4, delay: 0.6 },
        { top: "65%", right: "9%", size: 6, delay: 1.8 },
        { top: "45%", left: "14%", size: 3, delay: 2.1 },
        { top: "38%", right: "15%", size: 3, delay: 0.9 },
      ].map((dot, i) => (
        <motion.span
          animate={{ y: [0, -8, 0], opacity: [0.35, 0.7, 0.35] }}
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-[hsl(var(--gold))]"
          key={i}
          style={{
            top: dot.top,
            left: "left" in dot ? dot.left : undefined,
            right: "right" in dot ? dot.right : undefined,
            width: dot.size,
            height: dot.size,
          }}
          transition={{
            duration: 3 + i * 0.4,
            delay: dot.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Headline block */}
      <div className="relative max-w-3xl text-center">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden sm:h-24 sm:w-24 md:h-28 md:w-28"
          initial={{ opacity: 0, y: 18 }}
          transition={{ duration: 0.55, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
        >
          <img alt="AskJyotish logo" className="h-full w-full object-contain" src="/app_logo.png" />
        </motion.div>

        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 sm:mt-5"
          initial={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-6xl md:text-7xl">
            ChatGPT for
            <br />
            <span className="bg-[linear-gradient(135deg,hsl(var(--maroon)),hsl(var(--ruby)),hsl(var(--gold)))] bg-clip-text text-transparent">
              Your Birth Charts
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-[hsl(var(--muted-foreground))] sm:max-w-xl sm:text-base md:text-lg">
            The most advanced AI Vedic Astrologer platform powered by leading AI
            like ChatGPT and Gemini. Add your birth details to receive precise,
            chart-aware readings. We follow a zero-profit philosophy—you pay
            only the exact AI costs with zero markup.
          </p>
        </motion.div>
      </div>

      {/* Core features */}
      <FeatureCards />
    </section>
  );
}

// ─── Onboarding preview section ───────────────────────────────────────────────

function OnboardingPreview() {
  return (
    <section
      className="scroll-mt-28 py-12 sm:py-16 md:py-20 lg:py-14"
      id="overview"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:max-w-[960px]">
        <FadeUp
          className="mx-auto mb-8 max-w-3xl text-center lg:mb-5"
          delay={0.05}
        >
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[hsl(var(--gold))]">
            Overview
          </p>
          <p className="mt-4 text-sm leading-7 text-[hsl(var(--muted-foreground))] sm:text-base">
            See how AskJyotish works. Set up profiles for you and your family,
            add a small balance, and keep each person's readings and chats in
            one place.
          </p>
        </FadeUp>
        <FadeUp delay={0.1}>
          <OnboardingCarousel />
        </FadeUp>
      </div>
    </section>
  );
}

const ASTROLOGER_CARDS = [
  {
    name: "AskJyotish Small",
    tagline: "In-house trained AI model",
    avatarUrl: "/astrologers/hora_parashara.jpg",
    pricePerQuestionPaise: 100,
    ratingAccuracy: 5,
    ratingSpeed: 1,
    ratingCost: 5,
  },
  {
    name: "ChatGPT Small",
    tagline: "ChatGPT from OpenAI",
    avatarUrl: "/astrologers/siddhanta_parashara.jpg",
    pricePerQuestionPaise: 200,
    ratingAccuracy: 4,
    ratingSpeed: 4,
    ratingCost: 4,
  },
  {
    name: "Gemini Small",
    tagline: "Google provided AI model",
    avatarUrl: "/astrologers/madhya_parashara.jpg",
    pricePerQuestionPaise: 500,
    ratingAccuracy: 4,
    ratingSpeed: 3,
    ratingCost: 3,
  },
  {
    name: "AskJyotish Medium",
    tagline: "In-house trained AI model",
    avatarUrl: "/astrologers/brihat_parashara.jpg",
    pricePerQuestionPaise: 300,
    ratingAccuracy: 5,
    ratingSpeed: 1,
    ratingCost: 4,
  },
] as const;

function StatBars({
  value,
  fillClass,
}: {
  value: number | null;
  fillClass: string;
}) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: 5 }, (_, index) => (
        <span
          className={`h-1.5 w-6 rounded-full ${value != null && index < value ? fillClass : "bg-[rgba(176,160,128,0.18)]"}`}
          key={index}
        />
      ))}
    </div>
  );
}

function AstrologerCard({
  astrologer,
}: {
  astrologer: (typeof ASTROLOGER_CARDS)[number];
}) {
  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-[rgba(116,102,70,0.12)] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(250,244,235,0.98))] shadow-[0_2px_12px_rgba(91,78,43,0.07)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(91,78,43,0.14)]">
      <div className="relative h-[320px] w-full overflow-hidden sm:h-[340px]">
        <img
          alt={astrologer.name}
          className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-[1.02]"
          src={astrologer.avatarUrl}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(204,151,14,0.16))]" />
        <div className="absolute right-4 top-4 rounded-full bg-[#c8960e] px-3 py-1 text-[11px] font-semibold text-white shadow-sm">
          AI
        </div>
      </div>

      <div className="flex flex-1 flex-col p-[18px]">
        <div className="flex flex-col items-center gap-1.5 py-3 text-center sm:py-3.5">
          <p className="font-philosopher text-[20px] font-semibold leading-snug text-[#2f2a22]">
            {astrologer.name}
          </p>
          <p className="max-w-[14rem] text-[12px] leading-5 text-[#8d8273]">
            {astrologer.tagline}
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 border-y border-[rgba(116,102,70,0.14)] py-3">
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9a8e7d]">
            Price per query (avg)
          </span>
          <span className="text-xl font-black text-[hsl(var(--maroon))]">
            {formatCurrencyInr(astrologer.pricePerQuestionPaise)}
          </span>
        </div>

        <div className="mt-3 space-y-2.5">
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
            <span className="text-[#9a8e7d]">Depth</span>
            <StatBars
              value={astrologer.ratingAccuracy}
              fillClass="bg-[hsl(var(--gold))]"
            />
          </div>
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
            <span className="text-[#9a8e7d]">Speed</span>
            <StatBars
              value={astrologer.ratingSpeed}
              fillClass="bg-[hsl(var(--maroon))]"
            />
          </div>
          <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
            <span className="text-[#9a8e7d]">Cost</span>
            <StatBars
              value={astrologer.ratingCost}
              fillClass="bg-[hsl(var(--gold))]"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AstrologersSection() {
  return (
    <section className="scroll-mt-28 py-16 md:py-20" id="astrologers">
      <div className="mx-auto max-w-7xl px-6">
        <FadeUp className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[hsl(var(--gold))]">
            Astrologers
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-[hsl(var(--ink))] md:text-4xl">
            Meet our AI astrologers.
          </h2>
        </FadeUp>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {ASTROLOGER_CARDS.map((astrologer, index) => (
            <FadeUp delay={0.08 * index} key={astrologer.name}>
              <AstrologerCard astrologer={astrologer} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  return (
    <section className="scroll-mt-28 py-20 md:py-24" id="pricing">
      <div className="mx-auto max-w-4xl px-6">
        <FadeUp className="text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[hsl(var(--gold))]">
            Pricing
          </p>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight text-[hsl(var(--ink))] md:text-5xl">
            Wholesale AI.{" "}
            <span className="text-[hsl(var(--gold))]">Honest Astrology.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-[hsl(var(--muted-foreground))] sm:text-base md:text-lg">
            No per minute billing. No subscription traps. You only pay the
            actual AI cost.
          </p>
        </FadeUp>

        <FadeUp delay={0.08}>
          <div className="mt-12 overflow-hidden rounded-[2rem] border border-[hsl(var(--line)/0.8)] bg-[linear-gradient(180deg,rgba(255,252,246,0.98),rgba(249,243,234,0.98))] p-7 shadow-[0_24px_70px_hsl(var(--maroon)/0.08)] md:p-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--gold)/0.12)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[hsl(var(--gold))] ring-1 ring-[hsl(var(--gold)/0.22)]">
              <Zap className="size-3.5" />
              Pay-at-cost model
            </div>

            <h3 className="mt-5 font-display text-2xl font-bold tracking-tight text-[hsl(var(--ink))] md:text-3xl">
              The universal wallet.
            </h3>
            <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-[hsl(var(--muted-foreground))] sm:text-base">
              Add funds starting from ₹10. We deduct only the wholesale cost of
              your AI usage, down to the last paise. Your balance never
              expires, and there are no platform fees.
            </p>

            <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-[1.4rem] border border-[hsl(var(--line)/0.8)] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(250,244,235,0.98))] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                  No per minute billing
                </p>
                <p className="mt-2 text-sm leading-6 text-[hsl(var(--ink))]">
                  You are charged per question, not by the minute.
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-[hsl(var(--line)/0.8)] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(250,244,235,0.98))] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                  No subscriptions
                </p>
                <p className="mt-2 text-sm leading-6 text-[hsl(var(--ink))]">
                  No recurring plans or automatic renewals.
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-[hsl(var(--line)/0.8)] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(250,244,235,0.98))] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                  Zero markup
                </p>
                <p className="mt-2 text-sm leading-6 text-[hsl(var(--ink))]">
                  You pay the same wholesale rate we pay for AI compute.
                </p>
              </div>
              <div className="rounded-[1.4rem] border border-[hsl(var(--line)/0.8)] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(250,244,235,0.98))] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
                  No expiry
                </p>
                <p className="mt-2 text-sm leading-6 text-[hsl(var(--ink))]">
                  Your wallet balance stays available until you use it.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="scroll-mt-28 py-16 md:py-20" id="faq">
      <div className="mx-auto max-w-5xl px-6">
        <FadeUp className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[hsl(var(--gold))]">
            FAQs
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold text-[hsl(var(--ink))] md:text-4xl">
            Frequently asked questions
          </h2>
        </FadeUp>

        <FadeUp delay={0.08}>
          <FaqExplorer
            className="mt-10"
            items={LANDING_FAQ_ITEMS}
          />
        </FadeUp>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-[hsl(var(--line)/0.5)] bg-[hsl(var(--aura)/0.6)] py-8">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 px-6 text-sm text-[hsl(var(--muted-foreground))]">
        <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center overflow-hidden">
              <img
                alt="AskJyotish"
                className="h-full w-full object-contain"
                src="/app_logo.png"
              />
            </div>
            <span className="font-medium text-xl text-[hsl(var(--ink))]">
              AskJyotish
            </span>
          </div>
          <div className="flex gap-5">
            <a
              className="transition-colors hover:text-[hsl(var(--ink))]"
              href="/terms"
            >
              Terms
            </a>
            <a
              className="transition-colors hover:text-[hsl(var(--ink))]"
              href="/privacy"
            >
              Privacy
            </a>
            <Link
              className="hover:text-[hsl(var(--ink))] transition-colors"
              href="/contact"
            >
              Contact us
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function LandingPageContent() {
  const router = useRouter();
  const hasHydratedFromStorage = useAuthStore((s) => s.hasHydratedFromStorage);
  const status = useAuthStore((s) => s.status);
  const [activeSection, setActiveSection] = useState<string>("home");

  useEffect(() => {
    if (!hasHydratedFromStorage || status !== "authenticated") return;
    void routeToStartupDestination(router);
  }, [hasHydratedFromStorage, status, router]);

  useEffect(() => {
    if (
      !hasHydratedFromStorage ||
      status === "loading" ||
      status === "authenticated"
    )
      return;

    const sections = LANDING_SECTIONS.map((section) =>
      document.getElementById(section.id),
    ).filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length) return;

    let rafId = 0;

    const updateActiveSection = () => {
      const activationLine = window.innerHeight * 0.5;
      let currentSection = sections[0].id;

      for (const section of sections) {
        if (section.getBoundingClientRect().top <= activationLine) {
          currentSection = section.id;
        }
      }

      setActiveSection(currentSection);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateActiveSection);
    };

    updateActiveSection();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [hasHydratedFromStorage, status]);

  if (!hasHydratedFromStorage || status === "loading") {
    return <FullscreenLoadingScreen label="Loading AskJyotish…" />;
  }

  if (status === "authenticated") {
    return <FullscreenLoadingScreen label="Opening your workspace…" />;
  }

  return (
    <>
      <Nav activeSection={activeSection} onTabSelect={setActiveSection} />
      <main>
        <Hero />
        <OnboardingPreview />
        <AstrologersSection />
        <PricingSection />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}

export default LandingPageContent;
